import { createSelector } from '@reduxjs/toolkit';
import { isFreeUser, Role } from 'zl-shared/dist/auth';

import { AppState } from '../helper';
import { selectIsEducatorsAppEnabled } from './remoteConfig';

export const selectIsFreeUser = createSelector(
  (state: AppState) => state.user.role,
  (state: AppState) => state.user.subscription.active ?? false,
  (role, active) => isFreeUser(role as Role, { active }),
);

export const selectShouldShowAppOnboarding = createSelector(
  (state: AppState) => state.user.logged,
  (state: AppState) => state.user.isAppOnboarded,
  (state: AppState) => state.modals.appOnboardingModal,
  (logged, isAppOnboarded, modalState) => logged && !isAppOnboarded && !Object.values(modalState).every(Boolean),
);

export const selectCanDownloadMedia = createSelector(
  [
    (state: AppState) => state.user.logged,
    (state: AppState) => state.user.role,
    (state: AppState) => state.user.subscription.active,
    (state: AppState) => state.user.subscription.validUntil,
  ],
  (logged, role, active, validUntil) => {
    if (!logged) {
      return false;
    }
    if (![Role.USER, Role.VIP].includes(role as Role)) {
      return true;
    }
    return active && validUntil && Date.parse(validUntil) > Date.now();
  },
);

const SPECIAL_ROLES = new Set([Role.PARTNER, Role.HOST, Role.ADMIN, Role.MODERATOR]);

export const selectIsSpecialRole = createSelector(
  (state: AppState) => state.user.role,
  (role) => SPECIAL_ROLES.has(role as Role),
);

export const selectCanAccessEducatorsApp = createSelector(
  selectIsEducatorsAppEnabled,
  selectIsSpecialRole,
  (state: AppState) => state.user.persona,
  (state: AppState) => state.user.subscription?.features,
  (isEducatorsAppEnabled, isSpecialRole, persona, features) => {
    return !!isEducatorsAppEnabled && (isSpecialRole || features?.includes('educator') || persona === 'educator');
  },
);

export const selectHasEducatorFeature = createSelector(
  selectIsEducatorsAppEnabled,
  selectIsSpecialRole,
  (state: AppState) => state.user.subscription?.features,
  (isEducatorsAppEnabled, isSpecialRole, features) => {
    return !!isEducatorsAppEnabled && (isSpecialRole || features?.includes('educator'));
  },
);

export const selectIsReferralParticipant = createSelector(
  selectIsEducatorsAppEnabled,
  selectCanAccessEducatorsApp,
  (state: AppState) => state.user.isRealEmail,
  (state: AppState) => state.user.referralData?.viralLoops,
  (isEducatorsAppEnabled, canAccessEducatorsApp, isRealEmail, viralLoops) =>
    !!(
      isEducatorsAppEnabled &&
      isRealEmail &&
      (canAccessEducatorsApp || viralLoops?.referralCode || viralLoops?.referrer?.referralCode)
    ),
);

export const selectIsProfileCompleted = createSelector(
  (state: AppState) => state.user.name,
  (state: AppState) => state.user.ageGroup,
  (state: AppState) => state.user.persona,
  (name, ageGroup, persona) => !!name && !!ageGroup && !!persona,
);
